import type { CSSProperties } from 'react';
import type { DimensionScoreRecord } from '@sdd-maturity/shared-types';
import { levelLabel, scoreColor, levelBadgeClass } from '../lib/score';
import { buildLevelGradient } from '../lib/maturity-gradient';
import { DIMENSION_RUBRICS, formatDimension } from '../lib/rubrics';

interface DimensionCardProps {
  record: DimensionScoreRecord;
  /** AC-4.5: parent repo is a pre-v2 (v1-only) record — show legacy disclaimer. */
  isV1Legacy?: boolean;
  /** Set false to hide confidence row (e.g. Discovery dimensions have no confidence score) */
  showConfidence?: boolean;
  /**
   * This dimension's weight in the overall score, as an integer percent
   * (e.g. 25 for 25%). Optional — omit to hide the weight label (e.g. when
   * the caller hasn't wired a weight table yet).
   */
  weightPercent?: number;
  /**
   * This dimension isn't scored yet (no reliable source artifacts today) —
   * render "Coming Soon" in place of the level badge and "N/A" in place of the
   * score/confidence. See discovery-rubric.ts's `scored` flag.
   */
  comingSoon?: boolean;
  /**
   * Open the rubric explainer for this dimension. The parent owns the shared
   * <DimensionRubricDialog> so it can step through every dimension (prev/next).
   * The "?" button only renders when this is provided and a rubric exists.
   */
  onOpenRubric?: (dimension: string) => void;
}

/** Card displaying a single dimension's score, level, and confidence (AC-9.1) */
export function DimensionCard({
  record,
  isV1Legacy = false,
  showConfidence = true,
  weightPercent,
  comingSoon = false,
  onOpenRubric,
}: DimensionCardProps) {
  const { dimension, level, score, confidence } = record;
  const rubric = DIMENSION_RUBRICS[dimension];
  const title = formatDimension(dimension);
  const rubricLabel = `${title} rubric`;
  const showRubricButton = Boolean(rubric && onOpenRubric);
  // Keep the "?" glued to the title's last word: render the last word + button
  // as one no-wrap unit so the button follows the text (and wraps with it)
  // instead of detaching to its own line when the dimension name wraps.
  const lastSpace = title.lastIndexOf(' ');
  const titleLead = lastSpace >= 0 ? title.slice(0, lastSpace + 1) : '';
  const titleLast = lastSpace >= 0 ? title.slice(lastSpace + 1) : title;
  // Pale top wash tinted to this card's maturity level — the single-color
  // counterpart to the Overview cards' maturity-mix glow. Falls back to a plain
  // card when the level is unknown (buildLevelGradient returns null) or the
  // dimension isn't scored yet (a level tint would be meaningless).
  const gradient = comingSoon ? null : buildLevelGradient(level);

  return (
    <div
      className="card card-maturity-wash"
      data-testid={`dimension-card-${dimension}`}
      style={gradient ? ({ '--maturity-gradient': gradient } as CSSProperties) : undefined}
    >
      <div className="flex justify-between items-start gap-sm mb-md">
        {/* The "?" lives inside the h3 so it flows with the title text and stays
            attached to the last word when the name wraps. aria-label keeps the
            heading's accessible name to just the dimension title (the nested
            button is announced separately by its own label). */}
        <h3
          className="text-lg font-semibold text-primary"
          style={{ margin: 0 }}
          aria-label={
            showRubricButton || weightPercent !== undefined
              ? `${title}${weightPercent !== undefined ? `, weight ${weightPercent}%` : ''}`
              : undefined
          }
        >
          {showRubricButton ? (
            <>
              {titleLead}
              <span style={{ whiteSpace: 'nowrap' }}>
                {titleLast}
                {weightPercent !== undefined && (
                  <span
                    className="text-xs text-muted"
                    style={{ marginLeft: '6px', fontWeight: 400 }}
                    data-testid={`dimension-weight-${dimension}`}
                    aria-hidden="true"
                  >
                    ({weightPercent}%)
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onOpenRubric?.(dimension)}
                  aria-label={`Show rubric for ${rubricLabel}`}
                  aria-haspopup="dialog"
                  title="What does this score mean?"
                  style={{
                    verticalAlign: 'middle',
                    marginLeft: '6px',
                    background: 'none',
                    border: '1px solid var(--genai-sem-border)',
                    borderRadius: 'var(--genai-radius-full)',
                    width: '20px',
                    height: '20px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '11px',
                    color: 'var(--genai-sem-text-muted)',
                    lineHeight: 1,
                  }}
                >
                  ?
                </button>
              </span>
            </>
          ) : (
            <>
              {title}
              {weightPercent !== undefined && (
                <span
                  className="text-xs text-muted"
                  style={{ marginLeft: '6px', fontWeight: 400 }}
                  data-testid={`dimension-weight-${dimension}`}
                  aria-hidden="true"
                >
                  ({weightPercent}%)
                </span>
              )}
            </>
          )}
        </h3>
        {comingSoon ? (
          <span className="badge badge-neutral" data-testid="dimension-coming-soon-badge">
            Coming Soon
          </span>
        ) : (
          <span className={levelBadgeClass(level)}>{levelLabel(level)}</span>
        )}
      </div>

      {isV1Legacy && (
        <div
          className="text-xs text-muted"
          aria-label="v1 legacy classification — re-scan to update"
          data-testid="dimension-v1-legacy"
          style={{ marginBottom: 'var(--genai-space-sm)', fontStyle: 'italic' }}
        >
          v1 legacy classification — re-scan to refresh
        </div>
      )}

      {comingSoon ? (
        <div className="flex justify-between items-center">
          <div>
            <span className="text-2xl font-bold text-muted">N/A</span>
            <span className="text-base text-muted"> / 100</span>
          </div>
          <div className="text-base text-secondary">
            <span className="text-muted">Confidence: —</span>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <div>
            <span className="text-2xl font-bold" style={{ color: scoreColor(score) }}>{score}</span>
            <span className="text-base text-muted"> / 100</span>
          </div>
          {showConfidence && (
            <div className="text-base text-secondary">
              {confidence > 0 ? (
                <>
                  Confidence: {Math.round(confidence * 100)}%
                  {confidence < 0.5 && (
                    <span className="text-xs text-muted" style={{ marginLeft: '0.5rem' }} data-testid="low-confidence-badge">
                      · low confidence
                    </span>
                  )}
                </>
              ) : (
                <span className="text-muted">Confidence: —</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
